import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { parseReportSearch, type ReportSearch } from '../../features/reports/range';
import { FunnelReportScreen } from '../../features/reports/report-screens';
import { REPORT_PERMISSIONS } from '../../features/reports/types';
import { RequirePermission } from '../../lib/session/guards';
import { usePermissions } from '../../lib/session/permissions';

export const Route = createFileRoute('/reports/funnel')({
  validateSearch: (search: Record<string, unknown>): ReportSearch => parseReportSearch(search),
  component: FunnelReportPage,
});

/**
 * `/reports/funnel` — bookings and the booking funnel (FR-REPORT-01).
 *
 * `reports.funnel` opens it, which is the same key `GET /admin/reports/funnel` checks on
 * the server. The guard is a rendering decision and never the enforcement point
 * (rules §17.2, §22.2).
 */
function FunnelReportPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  /* One clock per mount, so a re-render never silently shifts "the last 30 days". */
  const now = useMemo(() => new Date(), []);
  const { can } = usePermissions();

  return (
    <RequirePermission keys={[REPORT_PERMISSIONS.funnel]}>
      <FunnelReportScreen
        search={search}
        onSearchChange={(next) => {
          void navigate({ to: '/reports/funnel', search: next, replace: true });
        }}
        now={now}
        can={can}
        backLink={
          <Link to="/reports" search={search} className="text-primary-accessible hover:underline">
            Reports
          </Link>
        }
      />
    </RequirePermission>
  );
}
